import { UIChunkType } from '@myre/app-agent-domain';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { CopierService } from '@myre/ui-primitive-core';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { type SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { isDataUIPart, type FileUIPart } from 'ai';

import { WORDINGS } from '../../../myre-wordings-loader';

import { AgentStatusComponent } from '../../agent-status/agent-status.component';
import {
  type ApprovalDecision,
  type Citation,
  type ClarificationAnswer,
  type MyreDataTypes,
  type MyreUiMessage,
  type MyreUiMessagePart,
} from '../../interfaces/chat.interfaces';
import { CitationModalService } from '../../services/citation-modal.service';
import {
  FormatService,
  type RenderSegment,
  type SegmentedParseResult,
} from '../../services/format.service';
import { HtmlPreviewService } from '../../services/html-preview.service';

import { ApprovalConfirmComponent } from '../approval-confirm/approval-confirm.component';
import { CitationSourcesComponent } from '../citation-sources/citation-sources.component';
import { CopyableBlockComponent } from '../copyable-block/copyable-block.component';
import { CtaButtonsComponent } from '../cta-buttons/cta-buttons.component';
import { DegradedNoticeComponent } from '../degraded-notice/degraded-notice.component';
import { FileAttachmentComponent } from '../file-attachment/file-attachment.component';
import { MessageTimestampComponent } from '../message-timestamp/message-timestamp.component';
import { NavigateConfirmComponent } from '../navigate-confirm/navigate-confirm.component';
import { QuestionOptionsComponent } from '../question-options/question-options.component';
import { QuotaWarningComponent } from '../quota-warning/quota-warning.component';
import { ResultBlockComponent } from '../result-block/result-block.component';

/**
 * Interactive data-part types that always render. Validated against
 * `MyreDataTypes`: each entry must be a real `data-<key>`, so a typo or a key
 * renamed in the registry is a compile error here, not a part that stops showing.
 */
const DISPLAYABLE_DATA_PART_TYPES = [
  UIChunkType.DataCta,
  UIChunkType.DataQuestion,
  UIChunkType.DataNavigate,
  UIChunkType.DataCitation,
  UIChunkType.DataApproval,
  UIChunkType.DataQuotaWarning,
  UIChunkType.DataResult,
  UIChunkType.DataDegraded,
] as const satisfies readonly `data-${keyof MyreDataTypes & string}`[];

/** Part types that are always displayable (incl. standalone interactive parts). */
const ALWAYS_DISPLAYABLE_TYPES: readonly string[] = [
  'file',
  'reasoning',
  ...DISPLAYABLE_DATA_PART_TYPES,
];

/** Checks if a part has displayable content */
function hasDisplayableContent(part: { type: string; text?: string }): boolean {
  if (ALWAYS_DISPLAYABLE_TYPES.includes(part.type)) {
    return true;
  }

  if (part.type === 'text') {
    return !!part.text?.trim();
  }

  return false;
}

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [
    CommonModule,
    AgentStatusComponent,
    CitationSourcesComponent,
    CopyableBlockComponent,
    CtaButtonsComponent,
    QuestionOptionsComponent,
    QuotaWarningComponent,
    DegradedNoticeComponent,
    NavigateConfirmComponent,
    ApprovalConfirmComponent,
    FileAttachmentComponent,
    MessageTimestampComponent,
    ResultBlockComponent,
  ],
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
  host: {
    '[class.user]': 'message().role === "user"',
    '[class.assistant]': 'message().role === "assistant"',
    '[class.error]': 'isError()',
    '[class.hidden]': '!_hasDisplayableContent()',
  },
})
export class ChatMessageComponent {
  private readonly _citationModalService = inject(CitationModalService);
  private readonly _copierService = inject(CopierService);
  private readonly _formatService = inject(FormatService);
  private readonly _htmlPreviewService = inject(HtmlPreviewService);
  private readonly _router = inject(Router);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  public readonly message = input.required<MyreUiMessage>();

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  public readonly showStatus = input<boolean>(false);
  public readonly statusText = input<string | null>(null);
  public readonly formattedTimestamp = input<string | null>(null);
  public readonly streaming = input<boolean>(false);
  public readonly isError = input<boolean>(false);
  /** A later message exists → a pending approval card here is dismissed. */
  public readonly superseded = input<boolean>(false);

  /** Session-bound "don't ask again" preference for navigation confirms. */
  public readonly navigateSkipConfirm = input<boolean>(false);

  /**
   * Whether the logged user is a Myre admin. Admins open the internal trace
   * visualizer; everyone else copies the trace reference to share with support.
   */
  public readonly isAdmin = input<boolean>(false);

  public readonly fileDownload = output<FileUIPart>();
  public readonly traceOpen = output<string>();
  public readonly retryRequested = output<void>();
  public readonly questionAnswered = output<ClarificationAnswer>();
  public readonly navigateDecided = output<ClarificationAnswer>();
  public readonly navigateSkipConfirmChange = output<boolean>();
  public readonly approvalConfirmed = output<ApprovalDecision>();
  public readonly approvalCancelled = output<void>();
  /** Bubble label override (shown instead of the raw text for picked options). */
  protected readonly _displayLabel = computed(
    () => this.message().metadata?.displayLabel,
  );

  /** Extract traceId from data-trace parts (if any) */
  protected readonly _traceId = computed(() => {
    const part = this.message().parts.find(
      p => p.type === UIChunkType.DataTrace,
    );

    return part ? this.getTraceId(part) : null;
  });

  /** Citations indexed by marker — drives inline [n] wrapping and click-through. */
  protected readonly _citationsByMarker = computed<
    ReadonlyMap<number, Citation>
  >(() => {
    const part = this.message().parts.find(
      (
        p,
      ): p is Extract<
        MyreUiMessagePart,
        { type: `${UIChunkType.DataCitation}` }
      > => isDataUIPart(p) && p.type === UIChunkType.DataCitation,
    );

    return new Map((part?.data.citations ?? []).map(c => [c.marker, c]));
  });

  /** Set of citation marker numbers present in this message's data-citation part. */
  protected readonly _citationMarkers = computed<ReadonlySet<number>>(
    () => new Set(this._citationsByMarker().keys()),
  );

  /** Whether the message has any displayable content */
  protected readonly _hasDisplayableContent = computed(
    () =>
      this.showStatus() ||
      this.streaming() ||
      this.message().parts.some(part => hasDisplayableContent(part)),
  );

  /**
   * Plain text of the WHOLE message, used by the footer copy button. Prefers
   * the bubble's display label override (e.g. a picked option) and otherwise
   * joins every text part, stripping the internal debrief block and the ```copy
   * fence delimiters (the inner content is kept, so the whole-message copy
   * reads cleanly even when the answer is split into copyable sub-blocks).
   */
  protected readonly _copyableText = computed(() => {
    const label = this._displayLabel();

    if (label) {
      return label;
    }

    return this.message()
      .parts.filter(
        (p): p is Extract<MyreUiMessagePart, { type: 'text' }> =>
          p.type === 'text',
      )
      .map(p => this._stripCopyFences(this._stripDebrief(p.text)).trim())
      .filter(text => text.length > 0)
      .join('\n\n');
  });

  /** Transient "copied" confirmation state, reset shortly after a copy. */
  protected readonly _copied = signal(false);
  private _copiedResetTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copies the message text to the clipboard and flashes a confirmation. */
  protected copyMessage(): void {
    const text = this._copyableText();

    if (!text || !this._copierService.copyText(text)) {
      return;
    }

    this._copied.set(true);

    if (this._copiedResetTimer !== null) {
      clearTimeout(this._copiedResetTimer);
    }

    this._copiedResetTimer = setTimeout(() => {
      this._copied.set(false);
      this._copiedResetTimer = null;
    }, 1500);
  }

  /** Transient "copied" confirmation for the non-admin trace copy. */
  protected readonly _traceCopied = signal(false);
  private _traceCopiedResetTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Trace action. Admins open the internal trace visualizer (via `traceOpen`);
   * everyone else copies a support-ready reference holding the trace id, since
   * that visualizer is an internal-only tool. A brief confirmation flashes.
   */
  protected onTraceClick(): void {
    const traceId = this._traceId();

    if (!traceId) {
      return;
    }

    if (this.isAdmin()) {
      this.traceOpen.emit(traceId);

      return;
    }

    const reference = `${this.chatWordings.traceReference()}: ${traceId}`;

    if (!this._copierService.copyText(reference)) {
      return;
    }

    this._traceCopied.set(true);

    if (this._traceCopiedResetTimer !== null) {
      clearTimeout(this._traceCopiedResetTimer);
    }

    this._traceCopiedResetTimer = setTimeout(() => {
      this._traceCopied.set(false);
      this._traceCopiedResetTimer = null;
    }, 1500);
  }

  /**
   * Matches the supervisor's internal debrief block regardless of where it
   * appears in the text.  The outer `[\s\S]*?` captures everything between the
   * opening `<details` and closing `</details>` non-greedily so we never span
   * across two separate details blocks.
   */
  private static readonly _DEBRIEF_RE =
    /<details[^>]*>[\s\S]*?<summary[^>]*>[\s\S]*?MVP Debrief[\s\S]*?<\/details>/gi;

  /** Cache for parsed text results to avoid re-parsing */
  private readonly _parseCache = new Map<string, SegmentedParseResult>();

  /**
   * Extracts the last debrief `<details>` block from all text parts of the
   * message as sanitized HTML, or `null` when none is present.  The block is
   * stripped from the source text before the markdown/HTML pipeline runs so it
   * never appears inline.
   */
  protected readonly _debrief = computed<SafeHtml | null>(() => {
    let last: string | null = null;

    for (const part of this.message().parts) {
      if (part.type !== 'text') {
        continue;
      }

      ChatMessageComponent._DEBRIEF_RE.lastIndex = 0;
      let match: RegExpExecArray | null;

      while (
        (match = ChatMessageComponent._DEBRIEF_RE.exec(part.text)) !== null
      ) {
        last = match[0];
      }
    }

    if (last === null) {
      return null;
    }

    return this._formatService.sanitizeHtml(last);
  });

  /** Strips all debrief blocks from a text string before rendering. */
  private _stripDebrief(text: string): string {
    ChatMessageComponent._DEBRIEF_RE.lastIndex = 0;

    return text.replace(ChatMessageComponent._DEBRIEF_RE, '').trim();
  }

  /**
   * Removes the ```copy fence delimiter lines while keeping their inner
   * content, mirroring how FormatService detects those fences (a line that is
   * exactly ```copy opens a block, the next bare ``` closes it). Used so the
   * whole-message copy yields clean text instead of literal fence markers.
   */
  private _stripCopyFences(text: string): string {
    let inCopy = false;

    return text
      .split('\n')
      .filter(line => {
        if (!inCopy && line === '```copy') {
          inCopy = true;

          return false;
        }

        if (inCopy && line === '```') {
          inCopy = false;

          return false;
        }

        return true;
      })
      .join('\n');
  }

  /**
   * Parses a text part into ordered render segments (markdown + copy blocks),
   * memoized per cleaned text + citation marker set. The cached array is
   * reference-stable, so the template's `@for` over it does not thrash.
   */
  private _parse(text: string): SegmentedParseResult {
    const clean = this._stripDebrief(text);
    const markers = this._citationMarkers();
    const cacheKey =
      markers.size > 0 ? `${clean}\x00${[...markers].sort().join(',')}` : clean;
    const cached = this._parseCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const result = this._formatService.parseSegments(
      clean,
      markers.size > 0 ? markers : undefined,
    );

    this._parseCache.set(cacheKey, result);

    return result;
  }

  /** Render segments for a text part, in source order. */
  protected segments(text: string): readonly RenderSegment[] {
    return this._parse(text).segments;
  }

  /**
   * Checks if the text contains HTML blocks that can be previewed.
   */
  protected hasHtmlBlock(text: string): boolean {
    return this._parse(text).lastHtmlBlock !== null;
  }

  /**
   * Opens the HTML preview in a detached window.
   * Triggered by user click to satisfy browser popup policies.
   */
  protected openHtmlPreview(text: string): void {
    const htmlBlock = this._parse(text).lastHtmlBlock;

    if (htmlBlock) {
      this._htmlPreviewService.preview(htmlBlock);
    }
  }

  /**
   * Converts a data-file part to a FileUIPart for the file-attachment component.
   */
  protected toFilePart(part: MyreUiMessagePart): FileUIPart | null {
    if (!isDataUIPart(part) || part.type !== UIChunkType.DataFile) {
      return null;
    }

    const inner = part.data.data;

    if (!inner?.url) {
      return null;
    }

    return {
      type: 'file',
      url: inner.url,
      filename: inner.fileName,
      mediaType: inner.mimeType,
    };
  }

  protected getTraceId(part: MyreUiMessagePart): string {
    if (!isDataUIPart(part) || part.type !== UIChunkType.DataTrace) {
      return '';
    }

    return part.data.traceId ?? '';
  }

  /**
   * Intercept clicks inside rendered markdown: inline [n] citation markers
   * open the evidence modal, internal links route through Angular instead of
   * doing a full page reload.
   */
  protected onMarkdownClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const marker = target.closest('sup.citation-marker');

    if (marker) {
      const n = Number(marker.getAttribute('data-citation-marker'));
      const citation = this._citationsByMarker().get(n);

      if (citation) {
        event.preventDefault();
        this._citationModalService.open(citation);
      }

      return;
    }

    const anchor = target.closest('a');

    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href');

    if (href?.startsWith('/')) {
      event.preventDefault();
      void this._router.navigateByUrl(href);
    }
  }
}
